import { combineReducers, configureStore } from "@reduxjs/toolkit"
import { createBrowserHistory } from "history"
import { createReduxHistoryContext } from "redux-first-history"
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from "redux-persist"
import storage from "redux-persist/lib/storage" // defaults to localStorage for web
import createSagaMiddleware from "redux-saga"

import rootReducer from "./rootReducer"
import rootSaga from "./rootSaga"
import immutablePersistenceTransform from "./Utils/ImmutablePersistenceTransform"
import { syncIncognitoActive } from "./Utils/incognito"

const persistConfig = {
  key: "chargeback",
  reducerVersion: "0.40.22", // Update this value every time you make changes to the reducers you want to persist.Staging
  storage,
  blacklist: ["router", "credits", "health", "requestsVerifiInform", "paymentMatching", "alertsSlice"], // reducers we don't want stored to persistence. Staging
  // Optionally, just specify the keys you DO want stored to persistence.
  // An empty array means 'don't store any reducers' -> infinitered/ignite#409
  // whitelist: [],
  transforms: [immutablePersistenceTransform]
}

const { createReduxHistory, routerMiddleware, routerReducer } = createReduxHistoryContext({
  history: createBrowserHistory()
})

export default async () => {
  const sagaMiddleware = createSagaMiddleware()

  const persistedReducer = persistReducer(
    persistConfig,
    combineReducers({
      ...rootReducer,
      router: routerReducer
    })
  )

  const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
          // temporary, while onSuccess/onError callbacks live in payloads — remove once those containers move to thunks
          ignoredActionPaths: ["payload.onSuccess", "payload.onError"]
        },
        immutableCheck: false // seamless-immutable still backs legacy slices; revisit once they're migrated
      }).concat(sagaMiddleware, routerMiddleware),
    devTools: process.env.NODE_ENV !== "production"
  })

  // Keep the non-React mirror of Incognito mode in sync (chart callbacks read it, see Utils/incognito).
  syncIncognitoActive(store.getState())
  store.subscribe(() => syncIncognitoActive(store.getState()))

  const history = createReduxHistory(store)

  const persistor = await persistStore(store)
  const reducerVersion = await storage.getItem("reducerVersion")

  if (reducerVersion !== persistConfig.reducerVersion) {
    await persistor.purge()
    await storage.setItem("reducerVersion", persistConfig.reducerVersion)
  }

  sagaMiddleware.run(rootSaga)

  return {
    store,
    persistor,
    history
  }
}
